package com.heelion.farmafreelance.agent

import kotlinx.datetime.LocalTime

/** I promemoria che l'utente può attivare o disattivare parlando con Farmy. */
enum class TipoPromemoria(val descrizione: String, val soloAndroid: Boolean = true) {
    TURNI_GIORNATA("i turni della giornata, ogni mattina"),
    TURNO_IMMINENTE("il turno imminente, la sera prima e 2 ore prima"),
    DA_CONFERMARE("i turni da confermare"),
    IN_ATTESA("i turni in attesa di risposta"),
    /** Evento sul telefono di un altro: lo manda il server come notifica push, anche sul web. */
    DISDETTE("le disdette della controparte", soloAndroid = false)
}

/** Quali promemoria sono attivi e a che ora; salvate sul dispositivo con [codifica]. */
data class PreferenzePromemoria(
    val attivi: Set<TipoPromemoria> = PREDEFINITI,
    /** Ora del promemoria "turni della giornata". */
    val oraGiornata: LocalTime = LocalTime(7, 30),
    /** Ora dei promemoria "da confermare" / "in attesa". */
    val oraSospesi: LocalTime = LocalTime(10, 0)
) {
    fun attivo(tipo: TipoPromemoria) = tipo in attivi

    /** Tipi di notifica push del server da non mandare a questo dispositivo (vedi DispositivoPush). */
    val tipiPushDisattivati: List<String>
        get() = if (attivo(TipoPromemoria.DISDETTE)) emptyList() else listOf("CANCEL")

    fun codifica(): String =
        "${attivi.joinToString(",") { it.name }};${oraGiornata};${oraSospesi}"

    companion object {
        val PREDEFINITI = setOf(TipoPromemoria.TURNO_IMMINENTE, TipoPromemoria.DA_CONFERMARE, TipoPromemoria.DISDETTE)

        fun decodifica(testo: String?): PreferenzePromemoria {
            if (testo.isNullOrBlank()) return PreferenzePromemoria()
            return try {
                val (tipi, giornata, sospesi) = testo.split(";")
                PreferenzePromemoria(
                    attivi = tipi.split(",").filter { it.isNotBlank() }.mapNotNull { n -> TipoPromemoria.entries.firstOrNull { it.name == n } }.toSet(),
                    oraGiornata = LocalTime.parse(giornata),
                    oraSospesi = LocalTime.parse(sospesi)
                )
            } catch (e: Exception) {
                PreferenzePromemoria()
            }
        }
    }
}

/** Cosa l'utente ha chiesto di cambiare: [tipi] vuoto = non si è capito quale promemoria. */
data class ImpostazionePromemoria(val tipi: Set<TipoPromemoria>, val attiva: Boolean, val ora: LocalTime?)

/**
 * Da una frase già classificata come PROMEMORIA_IMPOSTA ricava quale promemoria, se attivarlo o
 * disattivarlo e l'eventuale ora. Parole chiave e non modello: sono pochi casi ben definiti, e
 * così la risposta di Farmy ("Fatto, ti ricorderò…") descrive esattamente cosa ha cambiato.
 */
object EstrattorePromemoria {
    private val DISATTIVA = Regex("\\b(non|disattiva\\w*|togli\\w*|basta|smetti\\w*|spegni\\w*|niente|senza)\\b")
    private val TUTTI = listOf("tutti", "ogni promemoria", "qualsiasi")

    fun estrai(testo: String): ImpostazionePromemoria {
        val t = IntentClassifier.normalizza(testo)
        val attiva = !DISATTIVA.containsMatchIn(t)
        val tipi = when {
            TUTTI.any { t.contains(it) } && (t.contains("promemoria") || t.contains("avvis") || t.contains("notific")) ->
                TipoPromemoria.entries.toSet()
            else -> buildSet {
                if (Regex("disdic|disdett|annull|cancell|rinunc").containsMatchIn(t)) add(TipoPromemoria.DISDETTE)
                if (t.contains("confermare")) add(TipoPromemoria.DA_CONFERMARE)
                if (Regex("attesa|in sospeso|risposta").containsMatchIn(t)) add(TipoPromemoria.IN_ATTESA)
                if (Regex("giornata|del giorno\\b|di oggi|ogni mattina|la mattina|ogni giorno|tutti i giorni").containsMatchIn(t) &&
                    none { it == TipoPromemoria.DA_CONFERMARE || it == TipoPromemoria.IN_ATTESA }
                ) add(TipoPromemoria.TURNI_GIORNATA)
                if (Regex("imminent|sera prima|giorno dopo|prima del turno|ore prima").containsMatchIn(t)) add(TipoPromemoria.TURNO_IMMINENTE)
            }
        }
        return ImpostazionePromemoria(tipi, attiva, estraiOra(t))
    }

    /** "alle 7", "alle 7 30", "alle 7 e mezza", "alle 8 e un quarto" (testo già normalizzato). */
    private fun estraiOra(t: String): LocalTime? {
        val m = Regex("alle (\\d{1,2})(?: (?:e )?(\\d{2}|mezza|mezzo|un quarto))?\\b").find(t) ?: return null
        val h = m.groupValues[1].toIntOrNull()?.takeIf { it in 0..23 } ?: return null
        val min = when (val x = m.groupValues[2]) {
            "" -> 0
            "mezza", "mezzo" -> 30
            "un quarto" -> 15
            else -> x.toIntOrNull()?.takeIf { it in 0..59 } ?: 0
        }
        return LocalTime(h, min)
    }

    /** Applica la richiesta; l'ora vale per il tipo a cui si riferisce (giornata o sospesi). */
    fun applica(p: PreferenzePromemoria, i: ImpostazionePromemoria): PreferenzePromemoria {
        val attivi = if (i.attiva) p.attivi + i.tipi else p.attivi - i.tipi
        return p.copy(
            attivi = attivi,
            oraGiornata = if (i.attiva && i.ora != null && TipoPromemoria.TURNI_GIORNATA in i.tipi) i.ora else p.oraGiornata,
            oraSospesi = if (i.attiva && i.ora != null && (TipoPromemoria.DA_CONFERMARE in i.tipi || TipoPromemoria.IN_ATTESA in i.tipi)) i.ora else p.oraSospesi
        )
    }

    /** Conferma di Farmy dopo la modifica, o la domanda di chiarimento se il tipo non si è capito. */
    fun conferma(i: ImpostazionePromemoria, dopo: PreferenzePromemoria, promemoriaLocali: Boolean): String {
        if (i.tipi.isEmpty()) {
            return "Quale promemoria vuoi ${if (i.attiva) "attivare" else "disattivare"}? Posso ricordarti: " +
                TipoPromemoria.entries.joinToString("; ") { it.descrizione } + "."
        }
        val cosa = i.tipi.sortedBy { it.ordinal }.joinToString(", ") { descrizioneConOra(it, dopo) }
        val base = if (i.attiva) "Fatto! Ti ricorderò $cosa." else "Fatto, non ti ricorderò più $cosa."
        val soloApp = i.attiva && !promemoriaLocali && i.tipi.any { it.soloAndroid }
        return base + if (soloApp) " Nota: sul web funzionano solo gli avvisi di disdetta; gli altri promemoria arrivano sull'app Android." else ""
    }

    fun elenco(p: PreferenzePromemoria): String {
        val attivi = TipoPromemoria.entries.filter { p.attivo(it) }
        val spenti = TipoPromemoria.entries.filter { !p.attivo(it) }
        if (attivi.isEmpty()) return "Non hai promemoria attivi. Dimmi per esempio \"ricordami ogni mattina i turni della giornata\"."
        return "Ti ricordo: " + attivi.joinToString("; ") { descrizioneConOra(it, p) } + "." +
            if (spenti.isNotEmpty()) " Spenti: " + spenti.joinToString("; ") { it.descrizione } + "." else ""
    }

    private fun descrizioneConOra(t: TipoPromemoria, p: PreferenzePromemoria): String = when (t) {
        TipoPromemoria.TURNI_GIORNATA -> "i turni della giornata, ogni mattina alle ${formatta(p.oraGiornata)}"
        TipoPromemoria.DA_CONFERMARE, TipoPromemoria.IN_ATTESA -> "${t.descrizione}, alle ${formatta(p.oraSospesi)}"
        else -> t.descrizione
    }

    private fun formatta(o: LocalTime) = "${o.hour}:${o.minute.toString().padStart(2, '0')}"
}
